import { useState, useEffect } from 'react';
import { Trophy, CalendarBlank, Television, X } from '@phosphor-icons/react';

function WhereToWatch({ broadcasts, onClose }) {
  if (!broadcasts?.length) return null;
  return (
    <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#0B1F14] rounded-lg max-w-sm w-full p-6 relative" onClick={e => e.stopPropagation()} data-testid="where-to-watch-modal">
        <button onClick={onClose} className="absolute top-3 right-3 text-white/50 hover:text-white" data-testid="close-watch-modal">
          <X size={20} />
        </button>
        <div className="flex items-center gap-2 mb-4">
          <Television size={20} className="text-[#D4AF37]" />
          <h3 className="text-white text-base font-sans font-bold">Where to Watch</h3>
        </div>
        <div className="space-y-2">
          {broadcasts.map((bc, i) => (
            <div key={i} className="flex items-center gap-3 py-2.5 px-3 rounded-lg" style={{ background: 'rgba(255,255,255,0.06)' }}>
              <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                <Television size={14} className="text-[#D4AF37]" />
              </div>
              <span className="text-white text-sm font-sans font-semibold">{bc}</span>
            </div>
          ))}
        </div>
        <p className="text-white/30 text-[10px] font-sans mt-4 text-center">Check your local listings for availability</p>
      </div>
    </div>
  );
}

function SingleTournament({ event, onWhereToWatch }) {
  const top5 = (event.players || []).slice(0, 5);
  const hasPlayers = top5.length > 0;
  const isLive = event.state === 'in';
  const hasBroadcasts = event.broadcasts?.length > 0;

  return (
    <div className="relative z-10 flex flex-col justify-center h-full px-6 py-8">
      <div className="flex items-center gap-2 mb-1">
        <Trophy size={18} weight="fill" className="text-[#D4AF37]" />
        <span className="text-[10px] text-[#D4AF37] uppercase tracking-[0.2em] font-sans font-bold">
          {isLive ? 'Live Leaderboard' : event.state === 'post' ? 'Final Results' : 'Upcoming'}
        </span>
      </div>
      <h3 className="text-white text-lg font-sans font-bold leading-tight" style={{ textShadow: '0 1px 4px rgba(0,0,0,0.5)' }}>
        {event.tournament}
      </h3>
      <div className="flex items-center gap-2 mt-0.5 mb-4 flex-wrap">
        <span className="text-white/40 text-xs font-sans">{event.tour}</span>
        <span className="text-white/30">·</span>
        <span className={`text-xs font-sans font-semibold ${isLive ? 'text-green-400' : 'text-white/40'}`}>
          {event.status}
        </span>
        {isLive && hasBroadcasts && (
          <button
            onClick={() => onWhereToWatch(event.broadcasts)}
            className="ml-auto flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-600 hover:bg-red-500 transition-colors"
            data-testid="where-to-watch-btn"
          >
            <Television size={12} className="text-white" />
            <span className="text-white text-[10px] font-sans font-bold uppercase tracking-wider">Watch</span>
          </button>
        )}
      </div>

      {/* Broadcast chips for live events */}
      {isLive && hasBroadcasts && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {event.broadcasts.slice(0, 4).map((bc, i) => (
            <span key={i} className="px-2 py-0.5 rounded-full text-[9px] font-sans font-bold text-white/70 uppercase tracking-wider" style={{ background: 'rgba(255,255,255,0.08)' }}>
              {bc}
            </span>
          ))}
        </div>
      )}

      {hasPlayers ? (
        <div className="space-y-1">
          {top5.map((p, i) => (
            <div key={i} className="flex items-center justify-between py-2 px-3 rounded-lg" style={{ background: i === 0 ? 'rgba(212,175,55,0.12)' : 'rgba(255,255,255,0.04)' }}>
              <div className="flex items-center gap-3">
                <span className={`text-sm font-bold w-5 text-right ${i === 0 ? 'text-[#D4AF37]' : 'text-white/40'}`}>{p.position}</span>
                <span className="text-white text-sm font-sans font-semibold">{p.name}</span>
              </div>
              <span className={`text-sm font-bold ${
                p.score?.startsWith('-') ? 'text-red-400' : p.score === 'E' ? 'text-white' : 'text-white/50'
              }`}>
                {p.score || 'E'}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex items-center gap-3 py-4 px-3 rounded-lg" style={{ background: 'rgba(255,255,255,0.04)' }}>
          <CalendarBlank size={20} className="text-[#D4AF37]" />
          <div>
            <p className="text-white text-sm font-sans font-semibold">
              {new Date(event.start_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
              {event.end_date && ` - ${new Date(event.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}
            </p>
            {hasBroadcasts && (
              <p className="text-white/40 text-xs font-sans mt-0.5">
                Watch on {event.broadcasts.slice(0, 2).join(', ')}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function LeaderboardCard({ data }) {
  const [activeIdx, setActiveIdx] = useState(0);
  const [watchBroadcasts, setWatchBroadcasts] = useState(null);

  const tournaments = data?.tournaments || (data?.tournament ? [data] : []);

  useEffect(() => {
    if (tournaments.length <= 1) return;
    const timer = setInterval(() => {
      setActiveIdx(prev => (prev + 1) % tournaments.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [tournaments.length]);

  if (!tournaments.length) return null;

  const current = tournaments[activeIdx] || tournaments[0];

  return (
    <>
      {watchBroadcasts && <WhereToWatch broadcasts={watchBroadcasts} onClose={() => setWatchBroadcasts(null)} />}
      <div className="feed-leaderboard-item" data-testid="feed-leaderboard-card">
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, #0B3D1B 0%, #1B4332 40%, #0B1F14 100%)' }} />
        <SingleTournament event={current} onWhereToWatch={setWatchBroadcasts} />
        {tournaments.length > 1 && (
          <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-1.5 z-10">
            {tournaments.map((_, i) => (
              <button
                key={i}
                onClick={() => setActiveIdx(i)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${i === activeIdx ? 'bg-[#D4AF37] scale-125' : 'bg-white/20'}`}
              />
            ))}
          </div>
        )}
      </div>
    </>
  );
}
